import { Suspense, useMemo, useState } from 'react'
import { Box, Flex, Modal, ScrollArea, Stack } from '@mantine/core'
import { useListState } from '@mantine/hooks'
import { useTranslation } from 'react-i18next'
import { CenteredLoader } from '@/components/CenteredLoader'
import { DriveMapView } from './DriveMapView'
import { DriveMapLegend, type VehicleItem } from './DriveMapLegend'
import { DriveMapFilters, defaultDriveMapFilters, type DriveMapFilterValues } from './DriveMapFilters'
import { useDriveMapData, sameInterval, type DriveMapDrive } from './useDriveMapData'

type Props = {
  opened: boolean
  onClose: () => void
  startDate: Date
  endDate: Date
}

const SIDEBAR_WIDTH = 320

const buildVehicleItems = (vehicleColors: Record<string, string>): VehicleItem[] =>
  Object.entries(vehicleColors)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([id, color]) => ({ id, color, visible: true }))

function DriveMapBoundary({
  startDate,
  endDate,
  filters,
  setFilters,
}: {
  startDate: Date
  endDate: Date
  filters: DriveMapFilterValues
  setFilters: (next: DriveMapFilterValues) => void
}) {
  const { allDrives, selectableIntervals, vehicleColors } = useDriveMapData({ startDate, endDate })

  // useListState initializes once on mount. The boundary unmounts when the
  // modal closes, so reopening the modal resets visibility — which is the
  // intuitive default for a transient overlay.
  const [vehicleItems, vehicleItemHandlers] = useListState<VehicleItem>(buildVehicleItems(vehicleColors))

  const hiddenIds = useMemo(
    () => new Set(vehicleItems.filter(item => !item.visible).map(item => item.id)),
    [vehicleItems]
  )

  // Apply only the picker filters here. Vehicle visibility is handled by the
  // view via CSS so that toggling a single vehicle does not invalidate this
  // memo, re-key React elements, or churn the mapbox marker layer.
  const filteredDrives = useMemo<DriveMapDrive[]>(() => {
    return allDrives.filter(d => {
      if (filters.states.length > 0 && !filters.states.includes(d.drive_state)) return false
      if (filters.billingTypes.length > 0 && !filters.billingTypes.includes(d.billing_type)) return false
      if (filters.driveTypes.length > 0 && !d.drive_type.some(t => filters.driveTypes.includes(t))) return false
      if (filters.subInterval && !sameInterval(filters.subInterval, d.subInterval)) return false
      return true
    })
  }, [allDrives, filters])

  const driveCounts = useMemo(() => {
    const counts: Record<string, number> = {}
    for (const d of filteredDrives) {
      if (hiddenIds.has(d.vehicle_id)) continue
      counts[d.vehicle_id] = (counts[d.vehicle_id] ?? 0) + 1
    }
    return counts
  }, [filteredDrives, hiddenIds])

  return (
    <Flex h="100%">
      <Box
        w={SIDEBAR_WIDTH}
        style={{
          borderRight: '1px solid var(--mantine-color-default-border)',
          flex: `0 0 ${SIDEBAR_WIDTH}px`,
        }}
      >
        <ScrollArea h="100%" p="md">
          <Stack gap="md">
            <DriveMapFilters selectableIntervals={selectableIntervals} filters={filters} onFiltersChange={setFilters} />
            <DriveMapLegend items={vehicleItems} handlers={vehicleItemHandlers} driveCounts={driveCounts} />
          </Stack>
        </ScrollArea>
      </Box>
      <Box style={{ position: 'relative', flex: 1, minWidth: 0 }}>
        <DriveMapView drives={filteredDrives} vehicleColors={vehicleColors} hiddenIds={hiddenIds} />
      </Box>
    </Flex>
  )
}

export function DriveMapModal({ opened, onClose, startDate, endDate }: Props) {
  const { t } = useTranslation('scheduling')
  const [filters, setFilters] = useState<DriveMapFilterValues>(defaultDriveMapFilters())

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      fullScreen
      withCloseButton
      title={t('driveMap')}
      zIndex={10000}
      padding={0}
      styles={{
        body: { height: 'calc(100vh - 60px)', padding: 0 },
        header: { paddingInline: 16 },
      }}
    >
      <Suspense fallback={<CenteredLoader />}>
        <DriveMapBoundary startDate={startDate} endDate={endDate} filters={filters} setFilters={setFilters} />
      </Suspense>
    </Modal>
  )
}
